// src/components/ui/underline-tabs.tsx
//
// Shared "underline tabs" primitive used across modules (Fix Actions,
// Antenna, Cognition, Sandbox, Action Log sub-tabs, etc).
//
// Visual language:
//   - inactive tabs: muted-foreground text, transparent background
//   - active tabs:   foreground (black) text, bold, 4px teal underline bar
//
// Why this exists:
// The shadcn `ui/tabs.tsx` primitive renders a pill-style segmented
// control which is the wrong base for page-level mode selectors that
// we draw with underlines. Pages using shadcn Tabs directly end up
// overriding six-to-eight classes per trigger to defeat the pill
// styling. This component centralises those overrides in one place.
//
// Responsive behavior:
//  - Tab row horizontally scrolls when labels don't fit (hidden scrollbar).
//  - Active tab is auto-scrolled into view on mount and on change, so
//    the default-active tab doesn't leave later tabs permanently clipped.
//  - Font size, gaps, and padding clamp down on narrow viewports.
//  - Right-edge fade signals "more content" on clipped mobile rows.

import React, { useEffect, useRef } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./tabs";

export interface UnderlineTab {
  value: string;
  label: string;
  /**
   * Optional React node rendered inline after the label — use for small
   * inline adornments like count chips ("3 open"). For anything
   * structural, use a separate component instead.
   */
  labelExtra?: React.ReactNode;
}

export interface UnderlineTabsProps {
  /** Ordered list of tabs to render. */
  tabs: UnderlineTab[];
  /** Currently active tab value. */
  value: string;
  /** Fires when the user picks a different tab. */
  onChange: (value: string) => void;
  /** Tab panels. Each child should be a <UnderlineTabsPanel value="…">. */
  children: React.ReactNode;
  /**
   * Controls size. `"md"` (default) matches the module-level look used in
   * Fix Actions; `"sm"` matches the sub-module look used in Cognition and
   * sub-tab rows inside a page (e.g. Action Events / Deploy Tracking).
   */
  size?: "sm" | "md";
  /** Optional className for the outer wrapper (e.g. background, border). */
  className?: string;
  /**
   * Horizontal padding applied to the tab row. Defaults to
   * `"px-4 md:px-8"`. Pass `"px-0"` when the component is already inside
   * a padded parent.
   */
  padding?: string;
}

export function UnderlineTabs({
  tabs,
  value,
  onChange,
  children,
  size = "md",
  className = "",
  padding = "px-4 md:px-8",
}: UnderlineTabsProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Scroll active tab into view on mount AND on change. On mobile the
  // default-active tab sits at the left edge while later tabs clip off
  // the right; without an on-mount scroll nudge there's no affordance
  // that more tabs exist.
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const raf = requestAnimationFrame(() => {
      const activeEl = container.querySelector<HTMLElement>(
        `[data-state="active"]`
      );
      if (!activeEl) return;

      const cRect = container.getBoundingClientRect();
      const aRect = activeEl.getBoundingClientRect();
      const fullyVisible =
        aRect.left >= cRect.left && aRect.right <= cRect.right;
      if (fullyVisible) return;

      activeEl.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    });

    return () => cancelAnimationFrame(raf);
  }, [value]);

  const sizeConfig =
    size === "md"
      ? {
          padY: "pb-3 pt-4 md:pb-4 md:pt-6",
          font: "clamp(var(--text-sm), 1.5vw, var(--text-lg))",
          gap: "gap-4 md:gap-8",
        }
      : {
          padY: "pb-3 pt-0",
          font: "clamp(var(--text-sm), 2vw, var(--text-base))",
          gap: "gap-6 md:gap-8",
        };

  return (
    <Tabs
      value={value}
      onValueChange={onChange}
      className={`w-full ${className}`}
    >
      <div
        className="underline-tabs-wrap border-b relative"
        style={{
          borderColor: "var(--border)",
          backgroundColor: "var(--background)",
        }}
      >
        <style>{`
          .underline-tabs-scroll::-webkit-scrollbar { display: none; }

          .underline-tabs-wrap::after {
            content: '';
            position: absolute;
            top: 0;
            bottom: 1px;
            right: 0;
            width: 20px;
            pointer-events: none;
            background: linear-gradient(
              to right,
              rgba(0, 0, 0, 0),
              var(--background) 100%
            );
            z-index: 1;
          }
          @media (min-width: 768px) {
            .underline-tabs-wrap::after { display: none; }
          }

          .underline-tab-trigger {
            position: relative;
            color: var(--muted-foreground);
            transition: color 0.2s;
          }
          .underline-tab-trigger[data-state="active"] {
            color: var(--foreground);
            font-weight: var(--font-weight-bold);
          }
          .underline-tab-trigger[data-state="active"]::after {
            content: '';
            position: absolute;
            bottom: -1px;
            left: 0;
            right: 0;
            height: 4px;
            background-color: var(--alpha-teal);
          }
        `}</style>

        <div
          ref={scrollRef}
          className="underline-tabs-scroll"
          style={{
            overflowX: "auto",
            overflowY: "hidden",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            scrollBehavior: "smooth",
          }}
        >
          <TabsList
            className={`${padding} bg-transparent border-0 h-auto p-0 ${sizeConfig.gap} w-max min-w-full justify-start`}
            style={{ backgroundColor: "transparent" }}
          >
            {tabs.map((tab) => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className={`underline-tab-trigger rounded-none px-0 ${sizeConfig.padY} bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none border-0 whitespace-nowrap shrink-0 capitalize inline-flex items-center gap-2`}
                style={{
                  fontWeight: "var(--font-weight-semibold)",
                  fontSize: sizeConfig.font,
                }}
              >
                {tab.label}
                {tab.labelExtra}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
      </div>

      {children}
    </Tabs>
  );
}

/**
 * UnderlineTabsPanel — shadcn TabsContent with `mt-0` baked in so panels
 * sit flush below the tab row.
 */
export function UnderlineTabsPanel({
  value,
  children,
  className = "",
}: {
  value: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <TabsContent value={value} className={`mt-0 ${className}`}>
      {children}
    </TabsContent>
  );
}